import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';

import { ProductService } from 'app/services/product/product.service';
import { ProductDataDto } from 'app/models/product-data-dto';
import { ProductPickerModalComponent } from '../../productSearch/product-picker-modal/product-picker-modal.component';
import { labels } from 'app/mock-api/apps/mailbox/data';
import { values } from 'lodash';
import { ProductDayPickerModalComponent } from "../../productSearch/product-day-picker-modal/product-day-picker-modal.component";
import { ImageFallbackDirective } from "app/modules/admin/directives/ImageFallbackDirective";
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';

@Component({
  selector: 'app-menuedays',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, RouterModule, ProductDayPickerModalComponent, ImageFallbackDirective ,ImageUrlPipe],  
  templateUrl: './menuedays.component.html',
  styleUrl: './menuedays.component.scss'
})
export class MenuedaysComponent {

  private saveSubject = new Subject<any>();

  // UI state
  isLoading = false;
  pickerOpen = false;

  // Data
  products: ProductDataDto[] = [];
  filteredProducts: ProductDataDto[] = [];

  // Pagination
  currentPage = 1;
  itemsPerPage = 8;
  totalItems = 0;
  totalPages = 0;

  // Search
  isSearching = false;
  searchTerm = '';

  // ✅ Days filter
  days = [
    {label:'All',value:0},
    { label: 'Saturday', value: 1 },
    { label: 'Sunday', value: 2 },
    { label: 'Monday', value: 3 },
    { label: 'Tuesday', value: 4 },
    { label: 'Wednesday', value: 5 },
    { label: 'Thursday', value: 6 },
    { label: 'Friday', value: 7 },
  ];

  selectedDay:number= 0;

  constructor(
    private productService: ProductService,
    private router: Router
  ) {
    this.saveSubject.pipe(debounceTime(500)).subscribe((item) => {
      this.saveRow(item);
    });
  }

  ngOnInit() {
    this.loadByDay(1);
  }

  // ---------- Day filter ----------
  onDayChange() {
    this.currentPage = 1;
    this.loadByDay(1);
  }

  // ---------- Search ----------
  onSearch(event: any) {
    const term = (event.target.value || '').trim();

    if (!term) {
      this.isSearching = false;
      this.searchTerm = '';
      this.currentPage = 1;
      this.loadByDay(1);
      return;
    }

    this.isSearching = true;
    this.searchTerm = this.normalizeArabicText(term.toLowerCase());
    this.currentPage = 1;
    this.loadByDay(1);
  }

  private normalizeArabicText(text: string): string {
    return text
      .replace(/[أآإ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي');
  }

  // ---------- Load ----------
  loadByDay(page: number) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.currentPage = page;

    const request$ = (this.isSearching && this.searchTerm)
      ? this.productService.searchReservationProducts(this.searchTerm, page, this.itemsPerPage, this.selectedDay)
      : this.productService.getReservationProducts( page,this.selectedDay);

    request$.subscribe({
      next: (res: any) => {
        this.products = res?.data ?? [];
        this.filteredProducts = [...this.products];
        this.setMeta(res);

        this.isLoading = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      error: (err) => {
        console.error('Error loading products:', err);
        this.isLoading = false;
      }
    });
  }

  /** مرن مع اختلاف meta */
  private setMeta(res: any) {
    const meta = res?.meta ?? {};

    const total =
      meta.total ??
      meta.Total ??
      0;

    const lastPage =
      meta.lastPage ??
      meta.last_page ??
      meta.lastpage ??
      meta.totalPages ??
      meta.total_pages ??
      meta.pages ??
      0;

    this.totalItems = Number(total) || 0;

    const computed = Math.ceil((this.totalItems || 0) / (this.itemsPerPage || 1)) || 1;
    this.totalPages = Number(lastPage) || computed;
  }

  // ---------- Pagination ----------
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.loadByDay(page);
    }
  }

  previousPage() {
    if (this.currentPage > 1) this.loadByDay(this.currentPage - 1);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.loadByDay(this.currentPage + 1);
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    const safeTotalPages = this.totalPages || 1;

    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(safeTotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  }

  getStartIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getEndIndex(): number {
    const end = this.currentPage * this.itemsPerPage;
    return Math.min(end, this.totalItems);
  }

  // ---------- Qty Update (same as your style) ----------
  markDirty(item: any) {
    item._dirty = true;
  }

  onQtyChange(item: any) {
    item.min_qty = Number(item.min_qty ?? 0);
    item.prod_qty = Number(item.prod_qty ?? 0);
    item.max_qty = Number(item.max_qty ?? 0);
    item.meue_day_id = Number(item.meue_day_id ?? 0);

    if (item.max_qty < item.min_qty) item.max_qty = item.min_qty;
    if (item.menue_day_id <0 || item.menue_day_id > 7) alert("menue day must be from 0 to 7");

    this.saveSubject.next(item);
  } 

  private saveRow(item: any) {
    const payload = {
      id: item.id,
      min_qty: item.min_qty,
      prod_qty: item.prod_qty,
      max_qty: item.max_qty,
      menue_day_id: item.menue_day_id
    };

    this.productService.updateProductQty(payload).subscribe({
      next: () => {
        item._dirty = false;
        // Reload same page with same day filter
        this.loadByDay(this.currentPage);
      },
      error: (err) => {
        console.error(err);
        this.loadByDay(this.currentPage);
      }
    });
  }

selectedProduct: ProductDataDto = null;
showStockModal : boolean = false;
openStockModal(product: any) {
  // Clone عشان لو Cancel مايحفظش
  this.selectedProduct = JSON.parse(JSON.stringify(product));
  this.showStockModal = true;
}


@ViewChildren('stock_qty', { read: ElementRef }) stockQtyInputs!: QueryList<ElementRef>;
saveStock() {
  if (!this.validateStock()) {
    return;
  }

  const inputElements = this.stockQtyInputs.toArray();
  const stockPayload = [];

  for (let i = 0; i < this.selectedProduct.stock.length; i++) {
    const stk = this.selectedProduct.stock[i];
    const inputEl = inputElements[i]?.nativeElement as HTMLInputElement;

    // اقرأ القيمة من الـ input (حتى لو نفس alloc_qty)
    const stock_qty = inputEl ? (parseFloat(inputEl.value) || 0) : stk.alloc_qty;

    stockPayload.push({
      id: stk.id,
      alloc_qty: stk.alloc_qty,   // القيمة المعدّلة من allocated input (لو سمحنا بتعديلها)
      stock_qty: stock_qty        // القيمة من stocked input
    });
  }

  const payload = {
    product_id: this.selectedProduct.id,
    stock: stockPayload
  };

  console.log('Payload:', payload);

  // API CALL
  this.productService.updateProductStock(payload.product_id, payload).subscribe({
    next: (res: any) => {
      console.log('Success:', res);
      this.loadByDay(this.selectedDay);
      this.showStockModal = false ;
    },
    error: (err) => {
      console.error('Error saving stock:', err);
    }
  });
}


stockErrors: boolean[] = [];

validateStock(): boolean {
  this.stockErrors = []; // تأكد أن stockErrors معرف كـ boolean[] = [];
  const inputElements = this.stockQtyInputs.toArray();
  let isValid = true;

  if (!this.selectedProduct?.stock) return false;

  for (let i = 0; i < this.selectedProduct.stock.length; i++) {
    const stk = this.selectedProduct.stock[i];
    const inputEl = inputElements[i]?.nativeElement as HTMLInputElement;

    if (inputEl) {
      const stockedValue = parseFloat(inputEl.value) || 0;
      const allocated = stk.alloc_qty || 0;

      if (stockedValue < allocated) {
        this.stockErrors[i] = true;
        isValid = false;
      } else {
        this.stockErrors[i] = false;
      }
    }
  }

  return isValid;
}
  // ---------- Picker ----------
  onPickerSaved(payload: {
  day: number;
  items: Array<{ product_id: number; value: number }>;
}) {
  console.log(payload);

  this.productService.updateProductType(payload).subscribe({
    next: (res: any) => {
      if (res?.success) this.loadByDay(1);
    },
    error: () => {
      this.loadByDay(1);
    }
  });
}

  // ---------- Image fallback ----------
  handleImageError(event: any) {
    event.target.src = 'assets/images/logo/logo.svg';
  }
}


